// components
import { getRuntimeConfig } from '@/runtimeConfig.js';

import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';

const reactPlugin = new ReactPlugin();
const COOKIE_CONSENT_KEY = 'cookie-consent';
const COOKIE_CONSENT_MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000;

let appInsights = null;
let isSuperUserContext = false;
let userEmailContext = null;

export const setApplicationInsightsSuperUserContext = (isSuperUser) => {
  isSuperUserContext = Boolean(isSuperUser);
};

export const setApplicationInsightsUserEmailContext = (email) => {
  if (typeof email !== 'string') {
    userEmailContext = null;
    return;
  }

  const normalizedEmail = email.trim().toLowerCase();
  userEmailContext = normalizedEmail || null;
};

const parseCookieConsent = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  const storedConsent = localStorage.getItem(COOKIE_CONSENT_KEY);

  if (!storedConsent) {
    return null;
  }

  try {
    const parsedConsent = JSON.parse(storedConsent);

    if (
      parsedConsent?.consent === 'analytics' ||
      parsedConsent?.consent === 'essential'
    ) {
      return parsedConsent;
    }
  } catch {
    if (storedConsent === 'analytics' || storedConsent === 'essential') {
      return {
        consent: storedConsent,
        updatedAt: new Date(0).toISOString(),
      };
    }
  }

  return null;
};

const hasValidAnalyticsConsent = () => {
  const consentRecord = parseCookieConsent();

  if (!consentRecord || consentRecord.consent !== 'analytics') {
    return false;
  }

  const updatedTime = Date.parse(consentRecord.updatedAt);

  if (Number.isNaN(updatedTime)) {
    return false;
  }

  return Date.now() - updatedTime < COOKIE_CONSENT_MAX_AGE_MS;
};

export const initializeApplicationInsights = () => {
  if (appInsights) {
    return appInsights;
  }

  if (!hasValidAnalyticsConsent()) {
    return null;
  }

  // Get connection string from runtime config (fetched before app mounts)
  const connectionString = getRuntimeConfig().appInsightsConnectionString ?? '';

  // Only initialize Application Insights if a valid connection string is provided
  if (!connectionString || connectionString === 'connectionstring') {
    return null;
  }

  appInsights = new ApplicationInsights({
    config: {
      connectionString: connectionString,
      extensions: [reactPlugin],
      enableAutoRouteTracking: true,
      disableAjaxTracking: true,
    },
  });

  appInsights.addTelemetryInitializer((envelope) => {
    const isSuperUserValue = String(isSuperUserContext);
    const userEmailValue = userEmailContext;
    const baseData = envelope?.baseData || envelope?.data?.baseData;

    if (baseData) {
      if (!baseData.properties) {
        baseData.properties = {};
      }

      if (typeof baseData.properties.isSuperUser === 'undefined') {
        // App Insights custom dimensions are queried as strings in KQL.
        baseData.properties.isSuperUser = isSuperUserValue;
      }

      if (
        userEmailValue &&
        typeof baseData.properties.userEmail === 'undefined'
      ) {
        baseData.properties.userEmail = userEmailValue;
      }
    }

    // Fallback for telemetry shapes that carry custom dimensions on telemetryItem.data.
    if (!envelope.data || typeof envelope.data !== 'object') {
      envelope.data = {};
    }

    if (typeof envelope.data.isSuperUser === 'undefined') {
      envelope.data.isSuperUser = isSuperUserValue;
    }

    if (userEmailValue && typeof envelope.data.userEmail === 'undefined') {
      envelope.data.userEmail = userEmailValue;
    }

    // Additional fallback for envelopes using ext.properties.
    if (!envelope.ext || typeof envelope.ext !== 'object') {
      envelope.ext = {};
    }

    if (
      !envelope.ext.properties ||
      typeof envelope.ext.properties !== 'object'
    ) {
      envelope.ext.properties = {};
    }

    if (typeof envelope.ext.properties.isSuperUser === 'undefined') {
      envelope.ext.properties.isSuperUser = isSuperUserValue;
    }

    if (
      userEmailValue &&
      typeof envelope.ext.properties.userEmail === 'undefined'
    ) {
      envelope.ext.properties.userEmail = userEmailValue;
    }

    return true;
  });

  appInsights.loadAppInsights();

  return appInsights;
};

export { reactPlugin, appInsights };
